import { Box, Checkbox, Divider, FormControlLabel, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import { Controller, FormProvider, useForm } from "react-hook-form"

import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import type { ClassificationQueryableFields } from "@/graphql/codegen/graphql"
import { ProductCategoryFilter } from "@/screens/ManageProducts/components/ProductClasses/components/filters/ProductCategoryFilter"
import { ProductClassFilter } from "@/screens/ManageProducts/components/ProductClasses/components/filters/ProductClassFilter"
import { ProductManagerFilter } from "@/screens/ManageProducts/components/ProductClasses/components/filters/ProductManagerFilter"

interface FilterDrawerProps {
  showFilterDrawer: boolean
  toggleDrawer: (open: boolean) => void
}

type FormValues = ClassificationQueryableFields & {
  isNotAssociated: boolean
}

export const FilterDrawer: FC<FilterDrawerProps> = ({ toggleDrawer, showFilterDrawer }) => {
  const navigate = useNavigate({ from: "/app/admin/manage-products/classes" })
  const currentSearch = useSearch({
    from: "/app/admin/manage-products/_layout/classes",
    strict: true,
  })

  const { productManagerIds = [], categoryIds = [], classIds = [], isNotAssociated = false } = currentSearch

  const methods = useForm<FormValues>({
    values: {
      productManagerIds,
      categoryIds,
      classIds,
      isNotAssociated: isNotAssociated ?? false,
    },
  })

  const handleClearFilters = () => {
    methods.reset()
    navigate({
      search: (prev) => ({
        ...prev,
        categoryIds: undefined,
        classIds: undefined,
        companyIds: undefined,
        productManagerIds: undefined,
        isNotAssociated: undefined,
      }),
      replace: true,
    })
  }

  const handleFilterChange = (name: keyof FormValues, value: unknown) => {
    navigate({
      search: (prev) => {
        const val = Array.isArray(value) && value.length === 0 ? undefined : value

        return {
          ...prev,
          [name]: val || undefined,
        }
      },
      replace: true,
    })
  }

  return (
    <SearchFilterDrawer
      toggleDrawer={() => toggleDrawer(!showFilterDrawer)}
      open={showFilterDrawer}
      clear={handleClearFilters}
    >
      <FormProvider {...methods}>
        <Box className='flex h-full flex-col'>
          <Box className='flex h-1/4 flex-1 flex-col'>
            <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
              Product Category
            </Typography>
            <Divider />
            <ProductCategoryFilter onFilterChange={handleFilterChange} />
          </Box>

          <Box className='flex h-1/4 flex-1 flex-col'>
            <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
              Product Class
            </Typography>
            <Divider />
            <ProductClassFilter onFilterChange={handleFilterChange} />
          </Box>

          <Box className='flex h-1/4 flex-1 flex-col'>
            <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
              Product Manager
            </Typography>
            <Divider />
            <ProductManagerFilter onFilterChange={handleFilterChange} />
          </Box>

          <Box className='min-w-80 flex-none'>
            <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
              Non Associated
            </Typography>
            <Divider />
            <Controller
              name='isNotAssociated'
              control={methods.control}
              render={({ field }) => (
                <Box className='flex flex-col px-8 py-4'>
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked)
                          handleFilterChange(field.name, e.target.checked)
                        }}
                      />
                    }
                    label='Category and Class'
                  />
                </Box>
              )}
            />
          </Box>
        </Box>
      </FormProvider>
    </SearchFilterDrawer>
  )
}
